import apiClient, { PLAN_LIMIT_EVENT, getActivePrincipalId, type PlanLimitDetail } from './client';
import type {
  WorkItem,
  WorkItemAttachment,
  TenantAiSettings,
  UpdateTenantAiSettingsRequest,
  AiConnectionTestResult,
  DailyPlan,
  DailyPlanView,
  AnnualStrategicDriver,
  CalendarEvent,
  ExecutiveContext,
  PlanningPreference,
  ImageInput,
  ImageAnalysisResult,
  StartMyDayResponse,
  CloseDayResponse,
  CapacitySummary,
  Tag,
  Category,
  FocusWindow,
  CalendarSource,
  ExecutiveAssignment,
  MyAssistant,
  PromptConfiguration,
  AuditLogEntry,
  MeetingTranscript,
  BillingProduct,
  BillingInterval,
  BillingStatus,
  PlatformBillingProduct,
  PlatformBillingProductUpdate,
  PlatformBillingProductUpdateResult,
  PrincipalProfileEntry,
  PrincipalReminder,
  RestaurantResult,
  WebhookSubscription,
  Project,
  ProjectMember,
  ProjectOutcome,
  ProjectIndicator,
  IndicatorSnapshot,
  MeetingPrepResponse,
  MeetingBrief,
  MeetingBriefSummary,
  GenerateBriefRequest,
  PatchBriefRequest,
  OnboardingState,
  SetupStatus,
  PatchOnboardingStateRequest,
  ProtectedBlockPattern,
  CreateProtectedBlockRequest,
  ClaudeConnectionInfo,
  TimeAllocationReport,
  ActivityFeedItem,
  TenantIntegration,
  CalendarConflict,
  Decision,
  DailyFocusBlock,
  Idea,
  IdeaStatus,
  OutOfOffice,
  AuditActorFilter,
  AgentPermissions,
  UpdateAgentPermissionsRequest,
  AgentProposal,
  AgentProposalStatus,
  AgentProposalAcceptResult,
  AgentProposalBatchResult,
  ReviewSummary,
  VoiceCapture,
  VoiceCandidate,
  PatchVoiceCandidateRequest,
} from '../types/models';

// Work Items
export const workItemsApi = {
  /** `today` (yyyy-MM-dd, the person's date) picks which day's slots come back on open items. */
  getAll: (forExecId?: string, today?: string) =>
    apiClient.get<WorkItem[]>('/workitems', { params: { forExecutiveUserId: forExecId, today } }).then(r => r.data),
  getById: (id: string) => apiClient.get<WorkItem>(`/workitems/${id}`).then(r => r.data),
  getByDate: (date: string, forExecId?: string) => apiClient.get<WorkItem[]>(`/workitems/date/${date}`, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  getByStatus: (status: string) => apiClient.get<WorkItem[]>(`/workitems/status/${status}`).then(r => r.data),
  create: (data: Partial<WorkItem>) => apiClient.post<WorkItem>('/workitems', data).then(r => r.data),
  update: (id: string, data: Partial<WorkItem>) => apiClient.put<WorkItem>(`/workitems/${id}`, data).then(r => r.data),
  // Update many items in ONE request (status/priority/project/flags). Used by the
  // bulk-select toolbar so marking N items complete is a single call rather than
  // N parallel PUTs (which burst past the API rate limit — a 429 storm).
  bulkUpdate: (items: Array<{ id: string; status?: string; priority?: string; projectId?: string | null; flags?: string[] }>) =>
    apiClient.put<{ updated: number }>('/workitems/bulk', { items }).then(r => r.data),
  delete: (id: string) => apiClient.delete(`/workitems/${id}`),
  // Decision items completed with no outcome recorded — powers the Close Day
  // backstop. Its own endpoint because getAll() caps at 500 rows by CreatedAt
  // DESC, and decision items are typically old by creation date, so they fall
  // outside that window for an active user.
  getUnrecordedDecisions: (forExecId?: string) =>
    apiClient
      .get<WorkItem[]>('/workitems/decisions/unrecorded', {
        params: { forExecutiveUserId: forExecId },
      })
      .then(r => r.data),
  // Marks a decision as needing no record and completes it. Distinct from
  // skipping: skip defers (the item keeps surfacing in Close Day), this settles
  // it permanently.
  declineDecisionRecord: (id: string, forExecId?: string) =>
    apiClient
      .post<WorkItem>(`/workitems/${id}/decline-decision-record`, null, {
        params: { forExecutiveUserId: forExecId },
      })
      .then(r => r.data),
  // Completes a Type=Decision item AND records what was decided, atomically.
  // Deliberately separate from update(): plain `update(id, {status:'Done'})`
  // still completes with no capture and must keep working — that's the path
  // every shipped iOS/Mac build uses. See shared-docs/DECISION-CAPTURE-SPEC.md.
  completeWithDecision: (
    id: string,
    data: {
      outcome: string;
      rationale?: string;
      stakeholders?: string;
      evidence?: string;
      outcomeReviewDate?: string;
      /** Caller's LOCAL date (YYYY-MM-DD) — the server is UTC. */
      decisionDate?: string;
    },
    forExecId?: string,
  ) =>
    apiClient
      .post<WorkItem>(`/workitems/${id}/complete-with-decision`, data, {
        params: { forExecutiveUserId: forExecId },
      })
      .then(r => r.data),
  carryForward: (id: string, newDate: string) => apiClient.post<WorkItem>(`/workitems/${id}/carry-forward`, JSON.stringify(newDate)).then(r => r.data),
  /** "Still doing it": restarts an item's carry-over clock, so it's today's work again. */
  keep: (id: string, on: string) => apiClient.post<WorkItem>(`/workitems/${id}/keep`, null, { params: { on } }).then(r => r.data),
  delegate: (id: string, data: { targetExecutiveUserId: string; targetDate?: string; notes?: string }) =>
    apiClient.post<WorkItem>(`/workitems/${id}/delegate`, data).then(r => r.data),
  /** Stop waiting on the delegate: the item is back on your plan. */
  takeBack: (id: string) => apiClient.post<WorkItem>(`/workitems/${id}/take-back`).then(r => r.data),
  getRecurring: (forExecId?: string) =>
    apiClient.get<WorkItem[]>('/workitems/recurring', { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  // Attachments
  getAttachments: (workItemId: string) =>
    apiClient.get<WorkItemAttachment[]>(`/workitems/${workItemId}/attachments`).then(r => r.data),
  uploadAttachment: (workItemId: string, file: File, description?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);
    return apiClient.post<WorkItemAttachment>(`/workitems/${workItemId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  deleteAttachment: (workItemId: string, attachmentId: string) =>
    apiClient.delete(`/workitems/${workItemId}/attachments/${attachmentId}`),
  // Through the API client: its host, with the sign-in token. A plain link
  // went to the web app's own host with no token and never reached the file.
  downloadAttachment: (workItemId: string, attachmentId: string) =>
    apiClient.get<Blob>(`/workitems/${workItemId}/attachments/${attachmentId}/download`, { responseType: 'blob' })
      .then(r => r.data),
  // Dependencies
  setBlockedBy: (workItemId: string, blockedByWorkItemId: string | null) =>
    apiClient.put<WorkItem>(`/workitems/${workItemId}/blocked-by`, { blockedByWorkItemId }).then(r => r.data),
};

// Daily Plans
/** The server's OperatingRhythm enum: anything else is a 400. */
export type OperatingRhythm = 'StartMyDay' | 'Replan' | 'MiddayReset' | 'CloseDay' | 'PlanTomorrow' | 'WeeklyReset';

export const dailyPlanApi = {
  getByDate: (date: string, forExecId?: string) => apiClient.get<DailyPlan>(`/dailyplan/${date}`, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  /** keepReviewedPlan: the server refuses (409 plan_reviewed) rather than replace an approved, running or EA-prepared plan. */
  generate: (date: string, data?: { dailyTheme?: string; additionalInstructions?: string; trigger?: OperatingRhythm; keepReviewedPlan?: boolean }, forExecutiveUserId?: string | null) =>
    apiClient.post<StartMyDayResponse>(`/dailyplan/${date}/generate`, data || {}, { timeout: 60_000, params: { forExecutiveUserId } }).then(r => r.data),
  approve: (date: string) => apiClient.post<DailyPlan>(`/dailyplan/${date}/approve`).then(r => r.data),
  activate: (date: string) => apiClient.post<DailyPlan>(`/dailyplan/${date}/activate`).then(r => r.data),
  close: (date: string) => apiClient.post<DailyPlan>(`/dailyplan/${date}/close`).then(r => r.data),
  updateNotes: (date: string, notes: string) => apiClient.put<DailyPlan>(`/dailyplan/${date}/notes`, { notes }).then(r => r.data),
  updateHandoffNotes: (date: string, notes: string, forExecId?: string) =>
    apiClient.put<DailyPlan>(`/dailyplan/${date}/handoff-notes`, { notes }, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  updateExecutiveBriefing: (date: string, briefing: string, forExecId?: string) =>
    apiClient.put<DailyPlan>(`/dailyplan/${date}/executive-briefing`, { briefing }, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
};

// The day's saved plan — the one list of work every surface shows.
// forExecutiveUserId is added by the API client when an EA is acting for
// someone (see api/client.ts).
// One read of today's plan at a time per person: the Dashboard, the focus card
// and a live event asking at the same moment share it (on the day's first open,
// two reads used to race to make the plan). Keyed by whose plan it is, and the
// request names that person itself, so an EA who switches executive mid-read
// never gets the previous executive's plan.
const todayPlanInFlight = new Map<string, Promise<DailyPlanView>>();

export const planApi = {
  /** Today's plan, in the person's own time zone; made and saved if the day has none yet. */
  getToday: (): Promise<DailyPlanView> => {
    const principal = getActivePrincipalId();
    const key = principal ?? '';
    let read = todayPlanInFlight.get(key);
    if (!read) {
      // null = for the signed-in person (axios leaves it out of the URL).
      read = apiClient.get<DailyPlanView>('/plan/today', { params: { forExecutiveUserId: principal } })
        .then(r => r.data)
        .finally(() => { todayPlanInFlight.delete(key); });
      todayPlanInFlight.set(key, read);
    }
    return read;
  },
  /** Choose the day's items again. Added, in-progress and booked work stays. */
  replan: (date: string) => apiClient.post<DailyPlanView>(`/plan/${date}/replan`).then(r => r.data),
  /** Put an item on today's plan; replans never take it off. */
  addToday: (workItemId: string) => apiClient.post<DailyPlanView>(`/plan/today/items/${workItemId}`).then(r => r.data),
  /** Take an item off the day's plan; it keeps its date. */
  remove: (date: string, workItemId: string) => apiClient.delete<DailyPlanView>(`/plan/${date}/items/${workItemId}`).then(r => r.data),
};

// Operating Rhythm
//
// The action methods (startMyDay, replan, planTomorrow, closeDay) accept an
// optional forExecutiveUserId: the backend reads it as a query parameter so
// EAs can drive their executive's plan from the EA Workspace. When the body
// owner = caller (the executive themselves) we omit it and the API defaults
// to the caller's own plan. Splitting the param out of the body keeps the
// query string for principal-context routing and avoids polluting every DTO.
function rhythmParams(forExecutiveUserId?: string): { params?: Record<string, string> } {
  return forExecutiveUserId ? { params: { forExecutiveUserId } } : {};
}

// The three planning actions carry an AI call that runs 10-20s on its own
// (the backend caps it at 25s) on top of calendar sync and scheduling, so
// the default 30s client timeout would cancel a healthy request and log a
// 499 — which is what happened in May and why the AI budget was cut to
// 12s. Same precedent as transcriptApi below.
const PLANNING_TIMEOUT_MS = 60_000;

function planningParams(forExecutiveUserId?: string): { params?: Record<string, string>; timeout: number } {
  return { ...rhythmParams(forExecutiveUserId), timeout: PLANNING_TIMEOUT_MS };
}

// A plan made by rules because the AI allowance is used up still succeeds;
// its notice opens the same upgrade dialog as a refused AI request.
function announceAllowance(data: StartMyDayResponse): StartMyDayResponse {
  const notice = data?.aiAllowance;
  if (notice && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<PlanLimitDetail>(PLAN_LIMIT_EVENT, {
      detail: { code: 'ai_allowance_exceeded', message: notice.error, resetsAt: notice.resetsAt, background: false },
    }));
  }
  return data;
}

export const rhythmApi = {
  startMyDay: (data?: { date?: string; dailyTheme?: string; forExecutiveUserId?: string }) => {
    const { forExecutiveUserId, ...body } = data ?? {};
    return apiClient.post<StartMyDayResponse>('/operatingrhythm/start-my-day', body, planningParams(forExecutiveUserId)).then(r => announceAllowance(r.data));
  },
  replan: (data?: { date?: string; dailyTheme?: string; forExecutiveUserId?: string }) => {
    const { forExecutiveUserId, ...body } = data ?? {};
    return apiClient.post<StartMyDayResponse>('/operatingrhythm/replan', body, planningParams(forExecutiveUserId)).then(r => announceAllowance(r.data));
  },
  closeDay: (data: { date?: string; reflectionContent?: string; insights?: string; adjustmentsMade?: string; forExecutiveUserId?: string }) => {
    const { forExecutiveUserId, ...body } = data;
    return apiClient.post<CloseDayResponse>('/operatingrhythm/close-day', body, rhythmParams(forExecutiveUserId)).then(r => r.data);
  },
  planTomorrow: (data?: { dailyTheme?: string; forExecutiveUserId?: string }) => {
    const { forExecutiveUserId, ...body } = data ?? {};
    return apiClient.post<StartMyDayResponse>('/operatingrhythm/plan-tomorrow', body, planningParams(forExecutiveUserId)).then(r => announceAllowance(r.data));
  },
  getCapacity: (date: string) =>
    apiClient.get<CapacitySummary>(`/operatingrhythm/capacity/${date}`).then(r => r.data),
  suggestSlots: (duration: number, date: string, priority?: string) =>
    apiClient.get<{ suggestions: Array<{ startTime: string; endTime: string; score: number; reason: string }> }>(
      '/operatingrhythm/suggest-slots',
      { params: { duration, date, priority } }
    ).then(r => r.data),
  autoSchedule: (data: { date?: string; workItemIds?: string[]; createCalendarEvents?: boolean }) =>
    apiClient.post<{
      createdBlocks: Array<{ id: string; workItemId: string; date: string; startTime: string; endTime: string; workItem?: { title: string } }>;
      conflicts: string[];
      message: string;
    }>('/operatingrhythm/auto-schedule', data).then(r => r.data),
};

// Strategic Drivers
/** Time-weighted strategic alignment. `alignmentPercent` is absent when the
 *  week has no meeting or work minutes yet. */
export interface WeeklyAlignment {
  weekStart: string;
  weekEnd: string;
  alignmentPercent?: number | null;
  alignedMinutes: number;
  totalMinutes: number;
  meetings: { aligned: number; total: number };
  work: { aligned: number; total: number };
}

export const driversApi = {
  /** `forExecutiveUserId: null` = the signed-in person's own, whoever an EA is viewing. */
  getAll: (forExecutiveUserId?: string | null) =>
    apiClient.get<AnnualStrategicDriver[]>('/strategicdrivers', { params: { forExecutiveUserId } }).then(r => r.data),
  getActive: () => apiClient.get<AnnualStrategicDriver[]>('/strategicdrivers/active').then(r => r.data),
  getCurrent: () => apiClient.get('/strategicdrivers/current').then(r => r.data),
  getById: (id: string) => apiClient.get<AnnualStrategicDriver>(`/strategicdrivers/${id}`).then(r => r.data),
  create: (data: Partial<AnnualStrategicDriver>, forExecutiveUserId?: string | null) =>
    apiClient.post<AnnualStrategicDriver>('/strategicdrivers', data, { params: { forExecutiveUserId } }).then(r => r.data),
  update: (id: string, data: Partial<AnnualStrategicDriver>) => apiClient.put<AnnualStrategicDriver>(`/strategicdrivers/${id}`, data).then(r => r.data),
  delete: (id: string) => apiClient.delete(`/strategicdrivers/${id}`),
  updateLifecycle: (id: string, data: { status: string; reason?: string }) =>
    apiClient.put(`/strategicdrivers/${id}/lifecycle`, data).then(r => r.data),
  getArchived: (forExecId?: string) =>
    apiClient.get<AnnualStrategicDriver[]>('/strategicdrivers/archived', { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  getOrganisational: () =>
    apiClient.get<AnnualStrategicDriver[]>('/strategicdrivers/organisational').then(r => r.data),
  getAlignment: () =>
    apiClient.get('/strategicdrivers/alignment').then(r => r.data),
  /** This week's time on driver-linked work (Mon–Sun, the user's time zone). */
  getWeeklyAlignment: () =>
    apiClient.get<WeeklyAlignment>('/strategicdrivers/alignment/week').then(r => r.data),
  /** The same measure over an inclusive YYYY-MM-DD range. */
  getAlignmentForRange: (from: string, to: string) =>
    apiClient.get<WeeklyAlignment>('/strategicdrivers/alignment/time', { params: { from, to } }).then(r => r.data),
  getTimeInvested: () =>
    apiClient.get<{
      fyStart: string;
      useFinancialYear: boolean;
      timeInvested: Record<string, {
        self: { workItemMinutes: number; meetingMinutes: number };
        team: { workItemMinutes: number; meetingMinutes: number };
        // Per-contributor split. Null unless the caller is a tenant admin AND
        // the driver is organisational — null means "not permitted to see
        // this", an empty array means "nobody has logged time yet".
        byMember: {
          userId: string;
          name: string;
          isSelf: boolean;
          workItemMinutes: number;
          meetingMinutes: number;
        }[] | null;
      }>;
    }>('/strategicdrivers/time-invested').then(r => r.data),
};

// Calendar
export const calendarApi = {
  getByDate: (date: string, forExecId?: string) => apiClient.get<CalendarEvent[]>(`/calendar/${date}`, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  getByRange: (from: string, to: string, forExecId?: string | null) => apiClient.get<CalendarEvent[]>('/calendar/range', { params: { from, to, forExecutiveUserId: forExecId } }).then(r => r.data),
  sync: (range?: { from: string; to: string }) => apiClient.post<CalendarEvent[]>('/calendar/sync', null, { params: range }).then(r => r.data),
  linkToDriver: (id: string, data: { strategicDriverId?: string | null; applyToRecurring?: boolean }) =>
    apiClient.put(`/calendar/${id}/strategic-driver`, data).then(r => r.data),
  linkToProject: (id: string, data: { projectId?: string | null }) =>
    apiClient.put(`/calendar/${id}/project`, data).then(r => r.data),
  getMeetingPrep: (eventId: string, forExecId?: string) =>
    apiClient.get<MeetingPrepResponse>(`/calendar/event/${eventId}/prep`, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
};

// Onboarding — guided first-run state machine (spec §0). Resumable + skippable.
export const onboardingApi = {
  getState: () => apiClient.get<OnboardingState>('/onboarding/state').then(r => r.data),
  patchState: (data: PatchOnboardingStateRequest) =>
    apiClient.patch<OnboardingState>('/onboarding/state', data).then(r => r.data),
  skip: () => apiClient.post<OnboardingState>('/onboarding/skip').then(r => r.data),
  complete: () => apiClient.post<OnboardingState>('/onboarding/complete').then(r => r.data),
  claudeConnection: () => apiClient.get<ClaudeConnectionInfo>('/onboarding/claude-connection').then(r => r.data),
  /** "Tell me when {assistant} works" — one row per person per assistant. */
  joinWaitlist: (client: 'chatgpt' | 'copilot' | 'gemini') =>
    apiClient.post('/onboarding/waitlist', { client }).then(r => r.data),
  getProtectedBlocks: () => apiClient.get<ProtectedBlockPattern[]>('/onboarding/protected-blocks').then(r => r.data),
  createProtectedBlock: (data: CreateProtectedBlockRequest) =>
    apiClient.post<ProtectedBlockPattern>('/onboarding/protected-blocks', data).then(r => r.data),
  deleteProtectedBlock: (id: string) =>
    apiClient.delete(`/onboarding/protected-blocks/${id}`).then(r => r.data),
};

// Guided setup, part two: what Cadence has seen of the person's Claude setup.
export const setupApi = {
  getStatus: () => apiClient.get<SetupStatus>('/setup/status').then(r => r.data),
};

// EA invite links (guided setup §7.2). Preview and accept are anonymous.
export interface InvitationLink { id: string; url: string; expiresAt: string }
export interface OpenInvitation {
  id: string; role: string; executiveUserId: string; executiveName?: string | null;
  createdByName?: string | null; createdAt: string; expiresAt: string;
}
export interface InvitationPreview {
  organisation: string; invitedBy?: string | null; executiveName?: string | null; role: string; expiresAt: string;
}

export const invitationsApi = {
  create: (executiveUserId?: string) =>
    apiClient.post<InvitationLink>('/invitations', { role: 'ExecutiveAssistant', executiveUserId }).then(r => r.data),
  list: () => apiClient.get<OpenInvitation[]>('/invitations').then(r => r.data),
  revoke: (id: string) => apiClient.delete(`/invitations/${id}`).then(r => r.data),
  // The token goes in the body, never a URL that gets logged.
  preview: (token: string) => apiClient.post<InvitationPreview>('/invitations/preview', { token }).then(r => r.data),
  accept: (token: string, data: { displayName: string; email: string; password: string }) =>
    apiClient.post<{ email: string }>('/invitations/accept', { token, ...data }).then(r => r.data),
};

// Meeting Brief — first-class persisted prep on the calendar controller.
export const briefApi = {
  get: (eventId: string, forExecId?: string) =>
    apiClient.get<MeetingBrief>(`/calendar/event/${eventId}/brief`, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  generate: (eventId: string, data: GenerateBriefRequest, forExecId?: string) =>
    apiClient.post<MeetingBrief>(`/calendar/event/${eventId}/brief`, data, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  patch: (eventId: string, data: PatchBriefRequest, forExecId?: string) =>
    apiClient.patch<MeetingBrief>(`/calendar/event/${eventId}/brief`, data, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  list: (date: string, forExecId?: string) =>
    apiClient.get<MeetingBriefSummary[]>('/calendar/briefs', { params: { date, forExecutiveUserId: forExecId } }).then(r => r.data),
};

// Context
export const contextApi = {
  getAll: () => apiClient.get<ExecutiveContext[]>('/context').then(r => r.data),
  getByLayer: (layer: string) => apiClient.get<ExecutiveContext[]>(`/context/layer/${layer}`).then(r => r.data),
  create: (data: Partial<ExecutiveContext>) => apiClient.post<ExecutiveContext>('/context', data).then(r => r.data),
  update: (id: string, data: Partial<ExecutiveContext>) => apiClient.put<ExecutiveContext>(`/context/${id}`, data).then(r => r.data),
  delete: (id: string) => apiClient.delete(`/context/${id}`),
};

// Preferences
export const preferencesApi = {
  get: () => apiClient.get<PlanningPreference>('/preferences').then(r => r.data),
  update: (data: Partial<PlanningPreference>) => apiClient.put<PlanningPreference>('/preferences', data).then(r => r.data),
};

/** A fresh token pair for the signed-in person, as a refresh returns it. */
export interface SessionTokens {
  accessToken: string; refreshToken: string; expiresIn: number;
  displayName: string; role: string; tenantId: string; userId: string; isTenantAdmin: boolean;
}

// Auth / MFA / User Management
/** A sign-in's answer (login, welcome link, password change): tokens, or a two-step challenge first. */
export interface LoginResponse {
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  displayName?: string;
  role?: string;
  tenantId?: string;
  userId?: string;
  isTenantAdmin?: boolean;
  requiresMfa?: boolean;
  mfaToken?: string;
  mfaSuggested?: boolean;
  mfaRequired?: boolean;
}

/** How someone can sign in, besides an email link (`/auth/me`). */
export interface SignInMethods {
  password: boolean;
  passkeys: number;
  microsoft: boolean;
  google: boolean;
  apple: boolean;
  /** Their organisation has SSO for their email's domain. */
  sso: boolean;
}

/** Whether there's any way back in besides an email link. */
export function hasSignInMethod(m: SignInMethods | undefined): boolean {
  return !!m && (m.password || m.passkeys > 0 || m.microsoft || m.google || m.apple);
}

/** What the welcome page shows before the link is used. */
export interface WelcomePreview { firstName: string; email: string; organisationName: string | null }

/** Welcome links: preview without spending, then Continue to sign in once. The token never goes in a URL. */
export const welcomeApi = {
  preview: (token: string) => apiClient.post<WelcomePreview>('/auth/welcome/preview', { token }).then(r => r.data),
  accept: (token: string) => apiClient.post<LoginResponse>('/auth/welcome/accept', { token }).then(r => r.data),
};

export const authApi = {
  /**
   * A new set of two-step recovery codes, shown once; the old ones stop
   * working. Needs the authenticator code (`mfa_code_required` /
   * `wrong_mfa_code` otherwise) and two-step sign-in on (409 `mfa_not_enabled`).
   */
  replaceRecoveryCodes: (mfaCode: string) =>
    apiClient.post<{ recoveryCodes: string[]; message: string }>('/auth/mfa/recovery-codes', { mfaCode }).then(r => r.data),
  /**
   * Set a first password, or change it with the current one. A new way in
   * needs proof it's the person now: the two-step code when it's on, else a
   * recent sign-in for a first password (`reauth_required` otherwise). Ends
   * every other session, so it answers with a fresh one for this browser.
   */
  setPassword: (data: { currentPassword?: string; newPassword: string; mfaCode?: string }) =>
    apiClient.post<LoginResponse>('/auth/password', data).then(r => r.data),
  /**
   * Removes the linked Microsoft sign-in, with the same proof as linking: the
   * two-step code when it's on, else the current password, else a recent
   * sign-in. Ends every session, so it answers with a fresh one for this browser.
   */
  unlinkMicrosoft: (proof: SignInProof) =>
    apiClient.post<LoginResponse>('/auth/microsoft/unlink', proof).then(r => r.data),
  /**
   * Finishes linking Microsoft: the ticket the server sent back in the address's
   * fragment. Only this session's person can use it.
   */
  confirmMicrosoftLink: (ticket: string) =>
    apiClient.post<{ linked: string }>('/auth/microsoft/link/confirm', { ticket }).then(r => r.data),
  /** A sign-in in the last 15 minutes is enough; after that, the current password (when there is one). */
  setupMfa: (currentPassword?: string) =>
    apiClient.post<{ sharedKey: string; authenticatorUri: string }>('/auth/mfa/setup', currentPassword ? { currentPassword } : undefined).then(r => r.data),
  // Turning two-step on ends every session, this one's token included: the
  // response carries the new token pair, applied with applyTokens.
  enableMfa: (code: string) => apiClient.post<{ message: string; recoveryCodes: string[] } & SessionTokens>('/auth/mfa/enable', { code }).then(r => r.data),
  getUsers: () => apiClient.get<Array<{
    id: string; email: string; displayName: string; role: string;
    isActive: boolean; isTenantAdmin: boolean; createdAt: string; lastLoginAt: string | null; mfaEnabled: boolean;
  }>>('/auth/users').then(r => r.data),
  // Note: the old `onboardingSetup` helper that POSTed to
  // /onboarding/setup is gone — signup now goes through
  // `billingApi.checkout` so tenant provisioning is gated on a
  // successful Stripe payment + webhook. The server-side endpoint
  // now returns HTTP 410 Gone.
  /** An admin renames someone or changes their role (the server checks there's a seat for a new role). */
  updateUser: (id: string, data: { displayName: string; role: string }) =>
    apiClient.put(`/auth/users/${encodeURIComponent(id)}`, data).then(r => r.data),
  /** Make someone an organisation admin, or not. */
  setTenantAdmin: (id: string, isTenantAdmin: boolean) =>
    apiClient.put(`/auth/users/${encodeURIComponent(id)}/tenant-admin`, { isTenantAdmin }).then(r => r.data),
  me: () => apiClient.get<{
    id: string; email: string; displayName: string; tenantId: string; role: string; mfaEnabled: boolean;
    /** Ways to sign in besides an email link (older servers leave it out). */
    signInMethods?: SignInMethods;
  }>('/auth/me', { params: { forExecutiveUserId: null } }).then(r => r.data),
  resetPassword: (data: { email: string; token: string; newPassword: string }) =>
    apiClient.post<{ message?: string }>('/auth/reset-password', data).then(r => r.data),
  forgotPassword: (email: string) =>
    apiClient.post<{ message: string }>('/auth/forgot-password', { email }).then(r => r.data),
  revokeSessions: () =>
    apiClient.post<{ message: string } & SessionTokens>('/auth/revoke-sessions').then(r => r.data),
  revokeUserSessions: (userId: string) =>
    apiClient.post<{ message: string }>(`/auth/users/${userId}/revoke-sessions`).then(r => r.data),
  requestMagicLink: (email: string) =>
    apiClient.post('/auth/magic-link', { email }).then(r => r.data),
  /**
   * What deleting the signed-in account takes (GET /api/auth/me/deletion):
   * the password when two-factor is off, the authenticator code when it's
   * on, `none` for an account its organisation manages (single sign-on).
   * `canDelete` is false with `message` saying why (the only admin of an
   * organisation others still use). `closesOrganisation` is true when the
   * caller is the organisation's only member — deleting closes it too.
   * `handsOverTo` names who receives what they shared if they erase their
   * data (the organisation's owner); null when nobody else is left.
   */
  deletionRequirements: () =>
    apiClient.get<AccountDeletionRequirements>('/auth/me/deletion').then(r => r.data),
  /**
   * Self-account deletion (POST /api/auth/me/delete). The password or code
   * travels in the JSON body, never a URL or header. Keeps their data by
   * default: anonymises email/username, sets IsActive=false, rotates the
   * security stamp (kills all sessions). `hard=true` erases it: what they
   * shared goes to the organisation's owner (`handedOverTo`) and the rest
   * goes with the account.
   */
  deleteSelf: (confirmation: { password?: string; mfaCode?: string }, hard: boolean = false) =>
    apiClient.post<{
      message: string; hard: boolean; reassignedWorkItems: number; organisationClosed: boolean;
      subscriptionCancelled: boolean; appStoreSubscription: boolean; correlationId: string;
      handedOverTo?: string | null;
    }>('/auth/me/delete', { ...confirmation, hard }).then(r => r.data),
};

export interface AccountDeletionRequirements {
  confirmWith: 'mfa' | 'password' | 'none';
  canDelete: boolean;
  message: string | null;
  closesOrganisation: boolean;
  appStoreSubscription: boolean;
  /** Who receives what they shared if they erase their data; absent from older servers. */
  handsOverTo?: string | null;
}

// Passkeys (WebAuthn / FIDO2)
export interface PasskeyCredentialInfo {
  id: string;
  deviceName: string | null;
  credentialType: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

/**
 * Proof it's the person now, for adding or removing a way in (a password, a
 * passkey, a linked Microsoft account): the two-step code when it's on, else
 * the current password; with neither, a recent sign-in is the proof.
 */
export interface SignInProof { mfaCode?: string; currentPassword?: string }

export const passkeysApi = {
  /** Starts adding a passkey: a new way in, so it takes {@link SignInProof}. */
  registerOptions: (proof: SignInProof = {}) =>
    apiClient.post('/auth/passkey/register-options', proof).then(r => r.data),
  register: (data: { response: any; deviceName?: string }) =>
    apiClient.post<{ id: string; deviceName: string | null; createdAt: string }>('/auth/passkey/register', data).then(r => r.data),
  loginOptions: (email?: string) =>
    apiClient.post('/auth/passkey/login-options', { email }).then(r => r.data),
  login: (data: { response: any; challengeKey: string }) =>
    apiClient.post('/auth/passkey/login', data).then(r => r.data),
  list: () =>
    apiClient.get<PasskeyCredentialInfo[]>('/auth/passkey/credentials').then(r => r.data),
  /** Removes a passkey, with the same proof as adding one (never the last way in). */
  remove: (id: string, proof: SignInProof = {}) =>
    apiClient.delete(`/auth/passkey/credentials/${id}`, { data: proof }).then(r => r.data),
};

// Permissions
export interface EffectivePermissions {
  canManageStrategicDrivers: boolean;
  canManageProjects: boolean;
  canManageBilling: boolean;
  canManageUsers: boolean;
  canViewReports: boolean;
  canManageWebhooks: boolean;
  canManagePrompts: boolean;
  canExportData: boolean;
  /** When the organisation keeps delegation within teams: may delegate to anyone. */
  canDelegateAcrossTeams: boolean;
}

export interface PermissionOverrides {
  canManageStrategicDrivers: boolean | null;
  canManageProjects: boolean | null;
  canManageBilling: boolean | null;
  canManageUsers: boolean | null;
  canViewReports: boolean | null;
  canManageWebhooks: boolean | null;
  canManagePrompts: boolean | null;
  canExportData: boolean | null;
  canDelegateAcrossTeams: boolean | null;
  updatedAt: string;
}

export const permissionsApi = {
  getMyPermissions: () =>
    apiClient.get<EffectivePermissions>('/permissions/me').then(r => r.data),
  getUserPermissions: (userId: string) =>
    apiClient.get<{ effective: EffectivePermissions; overrides: PermissionOverrides | null }>(`/permissions/${userId}`).then(r => r.data),
  updateUserPermissions: (userId: string, data: Partial<Record<keyof EffectivePermissions, boolean | null>>) =>
    apiClient.put<{ message: string; effective: EffectivePermissions }>(`/permissions/${userId}`, data).then(r => r.data),
};

// API Keys
export const apiKeysApi = {
  /** A key is a new way in, so it takes {@link SignInProof} (as adding a passkey does). */
  create: (data: { name: string; expiryDays?: number; scopes?: string } & SignInProof) =>
    apiClient.post('/auth/api-keys', data).then(r => r.data) as Promise<{
      id: string; name: string; keyPrefix: string; key: string;
      expiresAt: string | null; message: string;
    }>,
  revoke: (id: string) => apiClient.delete(`/auth/api-keys/${id}`).then(r => r.data),
};

// Organisation → Security: sign-in rules for everyone. Reading needs Manage
// Users; changes need a tenant admin, signed in (never an API key), and are
// in the audit log.
export interface OrganisationSecuritySettings {
  /** "Require two-step sign-in for everyone" is on (company SSO sign-ins excepted). */
  requireTwoStep: boolean;
  /** Active people without two-step sign-in, who'd be asked to set it up. */
  peopleWithoutTwoStep: number;
}

export const organisationSecurityApi = {
  get: () =>
    apiClient.get<OrganisationSecuritySettings>('/organisation/security').then(r => r.data),
  /** On: anyone without two-step sign-in is sent to set it up at their next sign-in, or within the hour if signed in now. */
  setRequireTwoStep: (required: boolean) =>
    apiClient.put<OrganisationSecuritySettings>('/organisation/security/two-step', { required }).then(r => r.data),
  /**
   * Signs everyone in the organisation out except the admin asking: their web
   * and app sessions end within a minute and Claude connector sign-ins end.
   * API keys stay.
   */
  signOutEveryone: () =>
    apiClient.post<SignOutEveryoneResult>('/organisation/security/sign-out-everyone').then(r => r.data),
};

export interface SignOutEveryoneResult {
  /** People signed out (everyone active but the caller). */
  signedOut: number;
  /** Claude connector sign-ins ended. */
  connectorsSignedOut: number;
  /** "3 people were signed out." */
  message: string;
}

// Connections panel — Phase 3 task 3.7. Reads the API-key-authenticated
// activity log so an executive can see which MCP clients touched their
// tenant and what they did.
export interface ConnectionSummary {
  keyId: string;
  name: string;
  keyPrefix: string;
  scopes: string | null;
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  callsLast7Days: number;
  lastActivityAt: string | null;
  topPaths: Array<{ path: string; count: number }>;
}

export interface McpActivityEntry {
  id: string;
  apiKeyId: string;
  keyName: string;
  method: string;
  path: string;
  statusCode: number;
  timestamp: string;
}

export const connectionsApi = {
  list: () => apiClient.get<ConnectionSummary[]>('/connections').then(r => r.data),
  activity: (keyId: string, limit = 50) =>
    apiClient.get<McpActivityEntry[]>(`/connections/${keyId}/activity`, { params: { limit } }).then(r => r.data),
};

// OAuth consents — apps the user has connected via the OAuth flow (Claude
// Desktop, etc.). Distinct from API keys: the user did not paste a key, the
// app went through /oauth/authorize and the user consented.
export interface OAuthConsent {
  id: string;
  clientId: string;
  clientName: string;
  scopes: string;
  grantedAt: string;
  lastUsedAt: string | null;
}

export const oauthConsentsApi = {
  list: () => apiClient.get<OAuthConsent[]>('/oauth/consents').then(r => r.data),
  revoke: (id: string) => apiClient.delete(`/oauth/consents/${id}`).then(r => r.data),
};

// Bug / support reports — submitted by any authenticated user; triaged by
// platform admins. See `BugReportsController` for the full surface.
export type BugReportSeverity = 'Low' | 'Medium' | 'High' | 'Critical';
export type BugReportStatus = 'New' | 'Triaged' | 'InProgress' | 'Resolved' | 'WontFix';

export interface BugReport {
  id: string;
  title: string;
  description: string;
  severity: BugReportSeverity;
  status: BugReportStatus;
  pageUrl: string | null;
  userAgent: string | null;
  reporterDisplayName: string | null;
  reporterEmail: string | null;
  triageNotes: string | null;
  assignedToUserId: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  userId: string;
  tenantId: string;
}

export const bugReportsApi = {
  submit: (data: { title: string; description: string; severity?: BugReportSeverity; pageUrl?: string; userAgent?: string }) =>
    apiClient.post<BugReport>('/bug-reports', data).then(r => r.data),
  mine: () => apiClient.get<BugReport[]>('/bug-reports/mine').then(r => r.data),
  // Platform-admin only
  list: (params?: { status?: BugReportStatus; severity?: BugReportSeverity; limit?: number }) =>
    apiClient.get<BugReport[]>('/bug-reports', { params }).then(r => r.data),
  triage: (id: string, data: { status?: BugReportStatus; severity?: BugReportSeverity; assignedToUserId?: string; triageNotes?: string }) =>
    apiClient.put<BugReport>(`/bug-reports/${id}`, data).then(r => r.data),
};

// App releases — version tracking for the macOS / Windows / iOS clients.
// PlatformAdmin upserts a row whenever a new build ships; the desktop
// app polls /api/app/version/{platform} daily to detect updates.
export type AppPlatform = 'macos' | 'windows' | 'ios' | 'android';

export interface AppRelease {
  id: string;
  platform: AppPlatform;
  latestVersion: string;
  minimumVersion: string;
  downloadUrl: string;
  releaseNotes: string;
  publishedAt: string;
  updatedAt: string;
}

export interface AppReleaseUpsert {
  platform: AppPlatform;
  latestVersion: string;
  minimumVersion?: string;
  downloadUrl: string;
  releaseNotes?: string;
  publishedAt?: string;
}

export const appReleasesApi = {
  // Platform-admin only — full CRUD over the AppReleases table.
  list: () => apiClient.get<AppRelease[]>('/platform/releases').then(r => r.data),
  create: (data: AppReleaseUpsert) =>
    apiClient.post<AppRelease>('/platform/releases', data).then(r => r.data),
  update: (id: string, data: AppReleaseUpsert) =>
    apiClient.put<AppRelease>(`/platform/releases/${id}`, data).then(r => r.data),
  remove: (id: string) => apiClient.delete(`/platform/releases/${id}`),
  // Anonymous — what the desktop / mobile apps actually poll. Useful for
  // the admin UI's "verify what the client will see" preview.
  publicLookup: (platform: AppPlatform) =>
    apiClient.get<AppRelease>(`/app/version/${platform}`).then(r => r.data),
};

// Global search — Phase 3 task 3.12
export interface SearchHit {
  type: string;
  id: string;
  title: string;
  subtitle?: string;
  path: string;
  timestamp?: string;
}

export const searchApi = {
  query: (q: string, type?: string, limit = 25) =>
    apiClient.get<SearchHit[]>('/search', { params: { q, type, limit } }).then(r => r.data),
};

// SSO
export const ssoApi = {
  getConfigs: () => apiClient.get('/sso/config').then(r => r.data),
  createConfig: (data: { provider: string; displayName?: string; authority?: string; clientId?: string; clientSecret?: string; metadataUrl?: string; emailDomain: string }) =>
    apiClient.post('/sso/config', data).then(r => r.data),
  updateConfig: (id: string, data: Record<string, any>) =>
    apiClient.put(`/sso/config/${id}`, data).then(r => r.data),
  deleteConfig: (id: string) =>
    apiClient.delete(`/sso/config/${id}`).then(r => r.data),
  testConfig: (id: string) =>
    apiClient.post(`/sso/config/${id}/test`).then(r => r.data),
  /** How many people turning on Enforce SSO would sign out, for the domain being saved. */
  enforcementImpact: (id: string, domain?: string) =>
    apiClient.get<{ domain: string; signOutCount: number }>(`/sso/config/${id}/enforcement-impact`, { params: { domain } }).then(r => r.data),
  /**
   * Checks DNS for the configuration's TXT record (`cadence-verification=…`,
   * published on the email domain). Until it's found the domain's sign-ins
   * don't go to this SSO, it creates no accounts, and Enforce SSO can't be
   * turned on (409 `domain_not_verified`). A configuration from before
   * verification gets its record on the first check and keeps signing in the
   * organisation's own people meanwhile.
   */
  verifyDomain: (id: string) =>
    apiClient.post<SsoDomainVerificationResult>(`/sso/config/${id}/verify-domain`).then(r => r.data),
};

export interface SsoDomainVerificationResult {
  verified: boolean;
  domain: string | null;
  /** The TXT record value to publish, e.g. "cadence-verification=3f9a…". */
  record: string | null;
  /** What happened, in words for the admin. */
  message: string;
}

// Images
export const imagesApi = {
  getAll: () => apiClient.get<ImageInput[]>('/images').then(r => r.data),
  getById: (id: string) => apiClient.get<ImageInput>(`/images/${id}`).then(r => r.data),
  upload: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<ImageInput>('/images/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  analyse: (id: string) => apiClient.post<ImageAnalysisResult[]>(`/images/${id}/analyse`).then(r => r.data),
  accept: (id: string, items: ImageAnalysisResult[]) => apiClient.post<WorkItem[]>(`/images/${id}/accept`, items).then(r => r.data),
  discard: (id: string) => apiClient.post(`/images/${id}/discard`),
};

// Tags
export const tagsApi = {
  getAll: () => apiClient.get<Tag[]>('/tags').then(r => r.data),
  getById: (id: string) => apiClient.get<Tag>(`/tags/${id}`).then(r => r.data),
  create: (data: Partial<Tag>) => apiClient.post<Tag>('/tags', data).then(r => r.data),
  update: (id: string, data: Partial<Tag>) => apiClient.put<Tag>(`/tags/${id}`, data).then(r => r.data),
  delete: (id: string) => apiClient.delete(`/tags/${id}`),
};

// Categories
export const categoriesApi = {
  getAll: () => apiClient.get<Category[]>('/categories').then(r => r.data),
  getById: (id: string) => apiClient.get<Category>(`/categories/${id}`).then(r => r.data),
  create: (data: Partial<Category>) => apiClient.post<Category>('/categories', data).then(r => r.data),
  update: (id: string, data: Partial<Category>) => apiClient.put<Category>(`/categories/${id}`, data).then(r => r.data),
  delete: (id: string) => apiClient.delete(`/categories/${id}`),
};

// Focus Windows
export const focusWindowsApi = {
  getAll: () => apiClient.get<FocusWindow[]>('/focuswindows').then(r => r.data),
  getById: (id: string) => apiClient.get<FocusWindow>(`/focuswindows/${id}`).then(r => r.data),
  getByDay: (day: string) => apiClient.get<FocusWindow[]>(`/focuswindows/day/${day}`).then(r => r.data),
  create: (data: Partial<FocusWindow> & { writeToCalendar?: boolean; calendarSourceId?: string }) =>
    apiClient.post<FocusWindow>('/focuswindows', data).then(r => r.data),
  update: (id: string, data: Partial<FocusWindow>) => apiClient.put<FocusWindow>(`/focuswindows/${id}`, data).then(r => r.data),
  delete: (id: string) => apiClient.delete(`/focuswindows/${id}`),
};

// Daily Focus Blocks (real-time, date-specific — allocated by Start My Day or added manually)
export const dailyFocusBlocksApi = {
  getByDate: (date: string, forExecutiveUserId?: string | null) =>
    apiClient.get<DailyFocusBlock[]>('/dailyfocusblocks', { params: { date, forExecutiveUserId } }).then(r => r.data),
  /** Scheduled blocks in the range; `includeHonoured` adds completed ones
   *  (for totals of protected focus). */
  getByRange: (from: string, to: string, forExecutiveUserId?: string, includeHonoured?: boolean) =>
    apiClient.get<DailyFocusBlock[]>('/dailyfocusblocks/range', {
      params: { from, to, forExecutiveUserId, ...(includeHonoured ? { includeHonoured: true } : {}) },
    }).then(r => r.data),
  getById: (id: string) => apiClient.get<DailyFocusBlock>(`/dailyfocusblocks/${id}`).then(r => r.data),
  create: (data: { date: string; startTime: string; endTime: string; label?: string; linkedCalendarEventId?: string }) =>
    apiClient.post<DailyFocusBlock>('/dailyfocusblocks', data).then(r => r.data),
  // Notes: pass `null` to leave existing notes alone, an empty string to
  // explicitly clear them, a non-empty string to set them. Date/start/end
  // are required by the backend even when the user is only editing notes
  // — caller supplies the block's current values.
  update: (id: string, data: { date: string; startTime: string; endTime: string; label?: string; notes?: string | null }) =>
    apiClient.patch<DailyFocusBlock>(`/dailyfocusblocks/${id}`, data).then(r => r.data),
  delete: (id: string) => apiClient.delete(`/dailyfocusblocks/${id}`),
  markMeetingAsFocus: (calendarEventId: string) =>
    apiClient.post<DailyFocusBlock>(`/dailyfocusblocks/mark-meeting/${calendarEventId}`).then(r => r.data),
  unmarkMeetingAsFocus: (calendarEventId: string) =>
    apiClient.delete(`/dailyfocusblocks/unmark-meeting/${calendarEventId}`),
};

// Calendar Connect (OAuth)
export const calendarConnectApi = {
  connectMicrosoft: (data: { sourceId?: string; name?: string; isPersonal?: boolean; eaVisibility?: string; returnPath?: string }) =>
    apiClient.post('/calendarconnect/microsoft', data).then(r => r.data),
  connectGoogle: (data: { sourceId?: string; name?: string; isPersonal?: boolean; eaVisibility?: string; returnPath?: string }) =>
    apiClient.post('/calendarconnect/google', data).then(r => r.data),
  // `force=true` bypasses the wipe-alert backstop on the backend. Only set
  // it after the user has confirmed a 409 `wipeAlert: true` response from a
  // prior call — the per-row Reconnect button surfaces the confirmation.
  sync: (sourceId: string, opts?: { force?: boolean }) =>
    apiClient.post(`/calendarconnect/${sourceId}/sync`, null, opts?.force ? { params: { force: true } } : undefined).then(r => r.data),
  disconnect: (sourceId: string) =>
    apiClient.delete(`/calendarconnect/${sourceId}`).then(r => r.data),
};

// Calendar Sources
export const calendarSourcesApi = {
  getAll: () => apiClient.get<CalendarSource[]>('/calendarsources').then(r => r.data),
  create: (data: Partial<CalendarSource>) => apiClient.post<CalendarSource>('/calendarsources', data).then(r => r.data),
  update: (id: string, data: Partial<CalendarSource>) => apiClient.put<CalendarSource>(`/calendarsources/${id}`, data).then(r => r.data),
  updateSettings: (id: string, data: { isPersonal?: boolean; name?: string; eaVisibility?: string }) =>
    apiClient.put<CalendarSource>(`/calendarsources/${id}/settings`, data).then(r => r.data),
  delete: (id: string) => apiClient.delete(`/calendarsources/${id}`),
  sync: (id: string) => apiClient.post<CalendarSource>(`/calendarsources/${id}/sync`).then(r => r.data),
  createEvent: (sourceId: string, data: any) =>
    apiClient.post(`/calendarsources/${sourceId}/events`, data).then(r => r.data),
};

// Executive Assignments
export const executiveAssignmentsApi = {
  getAll: () => apiClient.get<ExecutiveAssignment[]>('/executiveassignments').then(r => r.data),
  /** Who can see your day: your assistants, and any an admin let see every executive. */
  mine: () => apiClient.get<MyAssistant[]>('/executiveassignments/mine').then(r => r.data),
  create: (data: Partial<ExecutiveAssignment>) => apiClient.post<ExecutiveAssignment>('/executiveassignments', data).then(r => r.data),
  // The backend deactivation route is DELETE /executiveassignments/{id}
  // (owner-or-admin gated). The old POST …/{id}/deactivate never existed
  // server-side and 404'd.
  deactivate: (id: string) => apiClient.delete<ExecutiveAssignment>(`/executiveassignments/${id}`).then(r => r.data),
};

// Prompt Configurations
export const promptConfigurationsApi = {
  getAll: () => apiClient.get<PromptConfiguration[]>('/promptconfigurations').then(r => r.data),
  // The server's routes are {functionKey}, {functionKey}/history and PUT
  // {id}/activate; a stray "key/" segment made the history panel always empty.
  getByKey: (key: string) => apiClient.get<PromptConfiguration>(`/promptconfigurations/${encodeURIComponent(key)}`).then(r => r.data),
  getHistory: (key: string) => apiClient.get<PromptConfiguration[]>(`/promptconfigurations/${encodeURIComponent(key)}/history`).then(r => r.data),
  create: (data: Partial<PromptConfiguration>) => apiClient.post<PromptConfiguration>('/promptconfigurations', data).then(r => r.data),
  activate: (id: string) => apiClient.put<PromptConfiguration>(`/promptconfigurations/${id}/activate`).then(r => r.data),
};

// Audit Logs — Phase 3 task 3.9
export const auditLogsApi = {
  /** actor 'agents': what agents did, including every API-key request (action 'Agent.Request'). */
  getAll: (params?: { action?: string; entityType?: string; from?: string; to?: string; limit?: number; actor?: AuditActorFilter }) =>
    apiClient.get<AuditLogEntry[]>('/auditlogs', { params }).then(r => r.data),
  exportCsv: (params?: { action?: string; entityType?: string; from?: string; to?: string; actor?: AuditActorFilter }) =>
    apiClient.get('/auditlogs/export/csv', { responseType: 'blob', params }).then(r => r),
};

// Agent permissions (Settings → Assistants & agents) — WebApp v4 plan, Phase 5.3.
// A signed-in person only (an agent is refused). Settings are "about you", so
// these default to the signed-in person (forExecutiveUserId: null) even while
// an EA is acting for someone; an EA may read an executive's, never change them.
export const agentPermissionsApi = {
  get: (forExecutiveUserId: string | null = null) =>
    apiClient.get<AgentPermissions>('/agentpermissions', { params: { forExecutiveUserId } }).then(r => r.data),
  /** Change some of them; fields left out stay as they are. */
  update: (changes: UpdateAgentPermissionsRequest) =>
    apiClient.put<AgentPermissions>('/agentpermissions', changes, { params: { forExecutiveUserId: null } }).then(r => r.data),
  /** Back to the defaults. */
  reset: () =>
    apiClient.delete<AgentPermissions>('/agentpermissions', { params: { forExecutiveUserId: null } }).then(r => r.data),
};

// Review: what agents asked to do (agent permissions set to Ask) — Phase 5.1.
// forExecutiveUserId left undefined follows the active principal (an EA acting
// for an executive sees and decides theirs); null means the signed-in person.
export const agentProposalsApi = {
  /** Pending by default, newest first. */
  list: (params?: { status?: AgentProposalStatus | 'All'; limit?: number; forExecutiveUserId?: string | null }) =>
    apiClient.get<AgentProposal[]>('/agentproposals', { params: { status: params?.status, limit: params?.limit, forExecutiveUserId: params?.forExecutiveUserId } }).then(r => r.data),
  /**
   * Do what the agent asked, as if the person had. If it can't be done now
   * (the item was deleted, the day has passed) the call fails with the reason
   * and the proposal stays pending with lastError; 409 when already decided or expired.
   */
  accept: (id: string, forExecutiveUserId?: string | null) =>
    apiClient.post<AgentProposalAcceptResult>(`/agentproposals/${id}/accept`, null, { params: { forExecutiveUserId } }).then(r => r.data),
  dismiss: (id: string, forExecutiveUserId?: string | null) =>
    apiClient.post<AgentProposal>(`/agentproposals/${id}/dismiss`, null, { params: { forExecutiveUserId } }).then(r => r.data),
  /** Up to 100 at once; each has its own outcome. */
  acceptMany: (ids: string[], forExecutiveUserId?: string | null) =>
    apiClient.post<AgentProposalBatchResult>('/agentproposals/accept', { ids }, { params: { forExecutiveUserId } }).then(r => r.data),
  dismissMany: (ids: string[], forExecutiveUserId?: string | null) =>
    apiClient.post<AgentProposalBatchResult>('/agentproposals/dismiss', { ids }, { params: { forExecutiveUserId } }).then(r => r.data),
};

// Transcripts
export const transcriptsApi = {
  getAll: (forExecId?: string) =>
    apiClient.get<MeetingTranscript[]>('/transcripts', { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  getById: (id: string) =>
    apiClient.get<MeetingTranscript>(`/transcripts/${id}`).then(r => r.data),
  upload: (data: { title: string; meetingDate: string; attendees?: string; transcriptText: string; calendarEventId?: string }, forExecId?: string) =>
    apiClient.post<MeetingTranscript>('/transcripts', data, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  // 100s axios timeout for the two AI-bearing calls. Default apiClient
  // timeout is 30s — fine for everything else, way too tight for transcript
  // analysis which empirically takes 60-90s end-to-end (Sonnet generates
  // dense structured JSON output sequentially). Backend has a matching 90s
  // cap so the server doesn't itself hang past Cloudflare's edge window.
  analyse: (id: string, autoAccept?: boolean, forExecId?: string) =>
    apiClient.post<MeetingTranscript>(
      `/transcripts/${id}/analyse`,
      {},
      { params: { autoAccept, forExecutiveUserId: forExecId }, timeout: 100_000 },
    ).then(r => r.data),
  autoAccept: (id: string, forExecId?: string) =>
    apiClient.post<{ transcript: MeetingTranscript; workItemsCreated: number }>(
      `/transcripts/${id}/auto-accept`,
      {},
      { params: { forExecutiveUserId: forExecId }, timeout: 100_000 },
    ).then(r => r.data),
  /**
   * Async variant — returns 202 immediately with the transcript in
   * Processing state. Completion arrives over SignalR as a
   * TranscriptAnalysed event. The SPA prefers this so the user isn't
   * blocked on a 60-second AI call.
   */
  autoAcceptAsync: (id: string, forExecId?: string) =>
    apiClient.post<{ transcript: MeetingTranscript; workItemsCreated: number }>(
      `/transcripts/${id}/auto-accept`,
      {},
      { params: { async: true, forExecutiveUserId: forExecId } },
    ).then(r => r.data),
  accept: (id: string, extractionIds: string[], forExecId?: string) =>
    apiClient.post(`/transcripts/${id}/accept`, { extractionIds }, { params: { forExecutiveUserId: forExecId } }),
  dismiss: (id: string, extractionIds: string[], forExecId?: string) =>
    apiClient.post(`/transcripts/${id}/dismiss`, { extractionIds }, { params: { forExecutiveUserId: forExecId } }),
  delete: (id: string, forExecId?: string) =>
    apiClient.delete(`/transcripts/${id}`, { params: { forExecutiveUserId: forExecId } }),
  assign: (
    id: string,
    data: { extractionIds: string[]; targetExecutiveUserId?: string; projectId?: string },
    forExecId?: string,
  ) =>
    apiClient.post<{ workItemsCreated: number; workItemIds: string[] }>(
      `/transcripts/${id}/assign`,
      data,
      { params: { forExecutiveUserId: forExecId } },
    ).then(r => r.data),
};


// Billing
export interface CheckoutSessionDetail {
  sessionId: string;
  status: string | null;
  paymentStatus: string | null;
  planName: string | null;
  organisationName: string | null;
  seatCount: number;
  maskedEmail: string;
  reference: string;
}

export const billingApi = {
  getPlans: () => apiClient.get<BillingProduct[]>('/billing/plans').then(r => r.data),
  checkout: (data: { productId: string; email: string; organisationName: string; executiveName: string; seatCount: number; interval?: BillingInterval }) =>
    apiClient.post<{ checkoutUrl: string }>('/billing/checkout', data).then(r => r.data),
  getCheckoutSession: (sessionId: string) =>
    apiClient.get<CheckoutSessionDetail>(`/billing/checkout-session/${encodeURIComponent(sessionId)}`).then(r => r.data),
  getStatus: () => apiClient.get<BillingStatus>('/billing/status').then(r => r.data),
  getPortalUrl: () => apiClient.post<{ portalUrl: string }>('/billing/portal').then(r => r.data),
};

/** Where a person's account stands (People's status chip). */
export type PersonStatus = 'active' | 'invited' | 'expired' | 'not_invited';

/** A person in the organisation, as People lists them (`GET /tenantadmin/users`). */
export interface OrgPerson {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  isTenantAdmin: boolean;
  mfaEnabled: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  jobTitle: string | null;
  status: PersonStatus;
  invitedAt: string | null;
  inviteExpiresAt: string | null;
}

// Tenant Administration
export const tenantAdminApi = {
  getTenant: () => apiClient.get('/tenantadmin/tenant').then(r => r.data),
  getUsers: () => apiClient.get<OrgPerson[]>('/tenantadmin/users').then(r => r.data),
  /** A new welcome link, withdrawing the last one. Only for people who haven't signed in yet. */
  sendInvite: (userId: string) =>
    apiClient.post<{ message: string; status: PersonStatus; invitedAt: string | null; inviteExpiresAt: string | null }>(
      `/tenantadmin/users/${encodeURIComponent(userId)}/invite`).then(r => r.data),
  // Tenant-admin user creation — enforces seats, creates the account with no
  // password and sends the welcome email so its owner sets their own.
  createUser: (data: { email: string; displayName: string; role?: string }) =>
    apiClient.post('/tenantadmin/users', data).then(r => r.data),
  getUsage: (year?: number, month?: number) =>
    apiClient.get('/tenantadmin/usage', { params: { year, month } }).then(r => r.data),
  // Bring-your-own AI provider. The key is write-only: GET returns a mask.
  getAiSettings: () => apiClient.get<TenantAiSettings>('/tenantadmin/ai-settings').then(r => r.data),
  updateAiSettings: (data: UpdateTenantAiSettingsRequest) =>
    apiClient.put<TenantAiSettings>('/tenantadmin/ai-settings', data).then(r => r.data),
  // One tiny request through the UNSAVED form values so a typo is caught before it is stored.
  testAiSettings: (data: UpdateTenantAiSettingsRequest) =>
    apiClient.post<AiConnectionTestResult>('/tenantadmin/ai-settings/test', data, { timeout: 30_000 }).then(r => r.data),
  deleteAiSettings: () => apiClient.delete('/tenantadmin/ai-settings').then(r => r.data),
  updateBillingEmail: (billingEmail: string) =>
    apiClient.put('/tenantadmin/billing-email', { billingEmail }).then(r => r.data),
  // Job title feeds the AI planning persona (AiPlanRequest.ExecutiveJobTitle).
  // Until this was exposed, the only way to set it was a raw API call, so
  // almost every account had none and every plan fell back to generic framing.
  setJobTitle: (userId: string, jobTitle: string) =>
    apiClient.put(`/tenantadmin/users/${userId}/hierarchy`, { jobTitle }).then(r => r.data),
  updateUserStatus: (userId: string, isActive: boolean) =>
    apiClient.put(`/tenantadmin/users/${userId}/status`, { isActive }).then(r => r.data),
  exportData: () =>
    apiClient.get('/tenantadmin/export', { responseType: 'blob' }).then(r => r),
  resetUserMfa: (userId: string, mfaCode: string) =>
    apiClient.post(`/tenantadmin/users/${userId}/reset-mfa`, {}, { headers: { 'X-MFA-Code': mfaCode } }).then(r => r.data),
  /** Without `permanently`, their account closes and their data is kept. With
   *  it, what they shared goes to `handOverTo` (the caller by default) and the
   *  rest of their data is erased. */
  deleteUser: (userId: string, mfaCode: string, permanently?: { handOverTo?: string }) =>
    apiClient.delete<{
      message: string; hard: boolean; reassignedWorkItems: number;
      handedOverTo?: string;
      handedOver?: { projects: number; workItems: number; decisions: number; drivers: number };
    }>(`/tenantadmin/users/${userId}`, {
      headers: { 'X-MFA-Code': mfaCode },
      params: permanently ? { hard: true, ...(permanently.handOverTo ? { handOverTo: permanently.handOverTo } : {}) } : undefined,
    }).then(r => r.data),
  updateSeats: (executiveSeats: number) =>
    apiClient.post('/tenantadmin/seats/update', { executiveSeats }).then(r => r.data),
  cancelTenant: (mfaCode: string) =>
    apiClient.post('/tenantadmin/cancel', {}, { headers: { 'X-MFA-Code': mfaCode } }).then(r => r.data),
  getBranding: () => apiClient.get('/tenantadmin/branding').then(r => r.data),
  updateBranding: (data: any) => apiClient.put('/tenantadmin/branding', data).then(r => r.data),
  getFinancialYear: () => apiClient.get('/tenantadmin/financial-year').then(r => r.data),
  updateFinancialYear: (data: { useFinancialYear: boolean; startMonth?: number }) =>
    apiClient.put('/tenantadmin/financial-year', data).then(r => r.data),
  getRetention: () => apiClient.get('/tenantadmin/retention').then(r => r.data),
  updateRetention: (dataRetentionDays: number | null) =>
    apiClient.put('/tenantadmin/retention', { dataRetentionDays }).then(r => r.data),
  downloadBackup: () =>
    apiClient.post('/tenantadmin/backup', null, { responseType: 'blob' }).then(r => r),
  restorePreview: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/tenantadmin/restore', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
};

// Reflections
export const reflectionsApi = {
  getByDate: (date: string) => apiClient.get(`/reflections/date/${date}`).then(r => r.data),
  getById: (id: string) => apiClient.get(`/reflections/${id}`).then(r => r.data),
  create: (data: { date: string; trigger: string; content: string; insights?: string; adjustmentsMade?: string }) =>
    apiClient.post('/reflections', data).then(r => r.data),
};

// Reporting
export const reportingApi = {
  getExecutiveReport: (from: string, to: string, forExecId?: string) =>
    apiClient.get('/reporting/executive', { params: { from, to, forExecutiveUserId: forExecId } }).then(r => r.data),
  getCrossExecutiveReport: (from: string, to: string) =>
    apiClient.get('/reporting/cross-executive', { params: { from, to } }).then(r => r.data),
  getCapacityForecast: (weeksAhead?: number) =>
    apiClient.get('/reporting/forecast', { params: { weeksAhead } }).then(r => r.data),
  getTimeAllocation: (from: string, to: string, forExecId?: string) =>
    apiClient.get<TimeAllocationReport>('/reporting/time-allocation', { params: { from, to, forExecutiveUserId: forExecId } }).then(r => r.data),
  getActivityFeed: (limit?: number, from?: string, forExecId?: string) =>
    apiClient.get<ActivityFeedItem[]>('/reporting/activity', { params: { limit, from, forExecutiveUserId: forExecId } }).then(r => r.data),
  getDelegationReport: (from: string, to: string, forExecId?: string) =>
    apiClient.get('/reporting/delegation', { params: { from, to, forExecutiveUserId: forExecId } }).then(r => r.data),
  getEaActivity: (from: string, to: string, executiveUserId?: string) =>
    apiClient.get('/reporting/ea-activity', { params: { from, to, executiveUserId } }).then(r => r.data),
};

// Platform Administration
export const platformApi = {
  getStats: () => apiClient.get('/platform/stats').then(r => r.data),
  getTenants: (status?: string, plan?: string) =>
    apiClient.get('/platform/tenants', { params: { status, plan } }).then(r => r.data),
  getTenant: (id: string) => apiClient.get(`/platform/tenants/${id}`).then(r => r.data),
  createTenant: (data: any) => apiClient.post('/platform/tenants', data).then(r => r.data),
  updateTenant: (id: string, data: any) => apiClient.put(`/platform/tenants/${id}`, data).then(r => r.data),
  // A tenant's own AI provider, set on their behalf (enterprise onboarding).
  getTenantAiSettings: (id: string) => apiClient.get<TenantAiSettings>(`/platform/tenants/${id}/ai-settings`).then(r => r.data),
  updateTenantAiSettings: (id: string, data: UpdateTenantAiSettingsRequest) =>
    apiClient.put<TenantAiSettings>(`/platform/tenants/${id}/ai-settings`, data).then(r => r.data),
  deleteTenantAiSettings: (id: string) => apiClient.delete(`/platform/tenants/${id}/ai-settings`).then(r => r.data),
  suspendTenant: (id: string) => apiClient.post(`/platform/tenants/${id}/suspend`).then(r => r.data),
  reactivateTenant: (id: string) => apiClient.post(`/platform/tenants/${id}/reactivate`).then(r => r.data),
  deleteTenant: (id: string, mfaCode: string) =>
    apiClient.delete(`/platform/tenants/${id}`, { headers: { 'X-MFA-Code': mfaCode } }).then(r => r.data),
  hardDeleteTenant: (id: string, mfaCode: string) =>
    apiClient.post(`/platform/tenants/${id}/hard-delete`, {}, { headers: { 'X-MFA-Code': mfaCode } }).then(r => r.data),
  getProducts: () => apiClient.get<PlatformBillingProduct[]>('/platform/products').then(r => r.data),
  createProduct: (data: Omit<PlatformBillingProductUpdate, 'applyToExistingTenants'>) => apiClient.post<PlatformBillingProduct>('/platform/products', data).then(r => r.data),
  updateProduct: (id: string, data: PlatformBillingProductUpdate) => apiClient.put<PlatformBillingProductUpdateResult>(`/platform/products/${id}`, data).then(r => r.data),
  syncProductToStripe: (id: string) => apiClient.post(`/platform/products/${id}/sync-stripe`).then(r => r.data),
  getEmailSettings: () => apiClient.get('/platform/email-settings').then(r => r.data),
  updateEmailSettings: (data: any) => apiClient.put('/platform/email-settings', data).then(r => r.data),
  sendTestEmail: (toEmail: string) => apiClient.post('/platform/email-settings/test', { toEmail }).then(r => r.data),
  getEmailLogs: (limit?: number) => apiClient.get('/platform/email-logs', { params: { limit } }).then(r => r.data),
  getUsers: (tenantId?: string) => apiClient.get('/platform/users', { params: { tenantId } }).then(r => r.data),
  updateUser: (userId: string, data: any) =>
    apiClient.put(`/platform/users/${userId}`, data).then(r => r.data),
  createPlatformAdmin: (data: any) => apiClient.post('/platform/admins', data).then(r => r.data),
  getStripeSettings: () => apiClient.get('/platform/stripe-settings').then(r => r.data),
  updateStripeSettings: (data: any) => apiClient.put('/platform/stripe-settings', data).then(r => r.data),
  getAiSettings: () => apiClient.get('/platform/ai-settings').then(r => r.data),
  updateAiSettings: (data: any) => apiClient.put('/platform/ai-settings', data).then(r => r.data),
  resendWelcomeEmail: (userId: string) => apiClient.post(`/platform/users/${userId}/resend-welcome`).then(r => r.data),
  getAuditLogs: (params?: { tenantId?: string; userId?: string; action?: string; from?: string; to?: string; limit?: number }) =>
    apiClient.get('/platform/audit-logs', { params }).then(r => r.data),
  resetUserPassword: (userId: string) => apiClient.post(`/platform/users/${userId}/reset-password`).then(r => r.data),
  getBillingOverview: () => apiClient.get('/platform/billing/overview').then(r => r.data),
  getBillingTenants: (params?: { status?: string; plan?: string }) =>
    apiClient.get('/platform/billing/tenants', { params }).then(r => r.data),
  getTenantBillingHistory: (tenantId: string) =>
    apiClient.get(`/platform/billing/tenants/${tenantId}/history`).then(r => r.data),
};

// Principal Profile
export const principalProfileApi = {
  getProfile: (execId: string) => apiClient.get<Record<string, PrincipalProfileEntry[]>>(`/principalprofile/${execId}`).then(r => r.data),
  getSection: (execId: string, section: string) => apiClient.get<PrincipalProfileEntry[]>(`/principalprofile/${execId}/section/${section}`).then(r => r.data),
  getReminders: (execId: string, daysAhead?: number) => apiClient.get<PrincipalReminder[]>(`/principalprofile/${execId}/reminders`, { params: { daysAhead } }).then(r => r.data),
  addEntry: (execId: string, data: Partial<PrincipalProfileEntry>) => apiClient.post<PrincipalProfileEntry>(`/principalprofile/${execId}`, data).then(r => r.data),
  updateEntry: (execId: string, entryId: string, data: Partial<PrincipalProfileEntry>) => apiClient.put<PrincipalProfileEntry>(`/principalprofile/${execId}/${entryId}`, data).then(r => r.data),
  deleteEntry: (execId: string, entryId: string) => apiClient.delete(`/principalprofile/${execId}/${entryId}`),
  seedTemplate: (execId: string) => apiClient.post<PrincipalProfileEntry[]>(`/principalprofile/${execId}/seed-template`).then(r => r.data),
};

// Restaurant Search
export const restaurantsApi = {
  search: (location: string, cuisine?: string, radius?: number) =>
    apiClient.get<RestaurantResult[]>('/restaurants/search', { params: { location, cuisine, radius } }).then(r => r.data),
};

// Webhooks
export const webhooksApi = {
  getAll: () => apiClient.get<WebhookSubscription[]>('/webhooks').then(r => r.data),
  create: (data: { url: string; event: string; secret?: string }) => apiClient.post<WebhookSubscription>('/webhooks', data).then(r => r.data),
  update: (id: string, data: { url?: string; isActive?: boolean }) => apiClient.put<WebhookSubscription>(`/webhooks/${id}`, data).then(r => r.data),
  delete: (id: string) => apiClient.delete(`/webhooks/${id}`),
};

// Notifications
export const notificationsApi = {
  getAll: (includeRead = false) =>
    apiClient.get('/notifications', { params: { includeRead } }).then(r => r.data) as Promise<Array<{
      id: string; title: string; message?: string; type: string;
      entityType?: string; entityId?: string; isRead: boolean;
      readAt?: string; createdAt: string;
    }>>,
  markAsRead: (id: string) =>
    apiClient.post(`/notifications/${id}/read`).then(r => r.data),
  markAllAsRead: () =>
    apiClient.post('/notifications/read-all').then(r => r.data),
  delete: (id: string) =>
    apiClient.delete(`/notifications/${id}`),
  getCoachingInsights: (forExecId?: string) =>
    apiClient.get<Array<{
      title: string;
      message: string;
      entityType?: string;
      entityId?: string;
    }>>('/notifications/coaching-insights', { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
};

// Horizon — quarterly rhythm rollup (Phase 3 task 3.1)
export interface HorizonResponse {
  label: string;
  quarter: number;
  quarterStart: string;
  quarterEnd: string;
  drivers: Array<{
    id: string;
    title: string;
    /** Work done this financial year; absent when nothing is linked. */
    progressPercentage?: number | null;
    progressDone?: number;
    progressTotal?: number;
    assessedProgressPercentage?: number;
    kpiCount: number;
    kpisOnTrack: number;
    kpisWithData?: number;
    kpiHealth?: 'Green' | 'Amber' | 'Red' | 'NoData';
  }>;
  kpis: Array<{
    id: string;
    name: string;
    unit?: string;
    currentValue?: number;
    targetValue?: number;
    baselineValue?: number;
    direction: string;
    driverId: string;
    driverTitle?: string;
    onTrack: boolean;
    /** Server KPI status; NoData KPIs are neither on track nor behind. */
    status?: 'Met' | 'OnTrack' | 'Behind' | 'NoData';
    progressToTarget?: number | null;
  }>;
  upcomingOutcomes: Array<{
    id: string;
    projectId: string;
    projectTitle: string;
    title: string;
    targetDate?: string;
    status: string;
  }>;
  weeklyCompletion: Array<{
    weekStart: string;
    weekEnd: string;
    planned: number;
    completed: number;
  }>;
}

export const horizonApi = {
  get: (forExecId?: string) =>
    apiClient.get<HorizonResponse>('/horizon', { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
};

// Delegation bundle (Phase 3 task 3.5)
export interface DelegationItem {
  id: string;
  title: string;
  status: string;
  priority: string;
  targetDate: string;
  assignedToUserId?: string;
  assignedToName?: string;
  delegatedByUserId?: string;
  delegatedByName?: string;
  driverId?: string;
  /** The work item this row opens for you (your own), when there is one. */
  openId?: string | null;
}

export interface DelegationBundle {
  delegatedByMe: DelegationItem[];
  delegatedToMe: DelegationItem[];
  drafts: DelegationItem[];
}

/** How someone relates to the delegator, closest first (DelegationPolicy.Group). */
export type DelegationGroup = 'Team' | 'Reports' | 'Manager' | 'Assistant' | 'Other';

export interface DelegationTarget {
  id: string;
  displayName: string;
  jobTitle?: string | null;
  role: string;
  group: DelegationGroup;
}

export interface DelegationTargets {
  /** Whose rules these are: the caller, or the executive an EA acts for. Never in `people`. */
  delegatorId?: string;
  /** The organisation keeps delegation within teams. */
  withinTeams: boolean;
  /** May delegate outside their teams anyway. */
  canCrossTeams: boolean;
  /** Everyone they may delegate to (only those), closest groups first. */
  people: DelegationTarget[];
}

export const delegationApi = {
  get: (forExecId?: string) =>
    apiClient.get<DelegationBundle>('/delegation', { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  /** Who the person (or the executive an EA acts for) may delegate to, grouped. The server enforces the same rule. */
  targets: (forExecId?: string | null) =>
    apiClient.get<DelegationTargets>('/delegation/targets', { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
};

// Teams (OrgTeam): nested teams anyone can be in several of, leads,
// reporting lines, the org chart and each team's numbers rolled up the tree.
export interface Team {
  id: string;
  name: string;
  description?: string | null;
  /** The team it sits under; null at the top. */
  parentTeamId?: string | null;
  leadUserId?: string | null;
  isArchived: boolean;
  memberIds: string[];
}

export interface TeamPerson {
  id: string;
  displayName: string;
  role: string;
  jobTitle?: string | null;
  reportsToUserId?: string | null;
  /** Only a tenant admin changes another admin account's teams or line. */
  isAdminAccount?: boolean;
  /** For an EA: the executives they support. */
  supportsUserIds: string[];
}

/** A team's numbers, rolled up down the tree (everyone counted once). */
export interface TeamMetrics {
  people: number;
  open: number;
  overdue: number;
  doneThisWeek: number;
  waitingOnOthers: number;
}

export interface TeamsView {
  teams: Team[];
  /** Everyone active in the organisation. */
  people: TeamPerson[];
  /** Per team id, for the teams the caller may see numbers for (admins; leads, down their tree). */
  metrics: Record<string, TeamMetrics>;
  /** May create, rename, move and archive teams, add and remove people, and set any line. */
  canManage: boolean;
  callerIsTenantAdmin?: boolean;
  myUserId: string;
  /** The organisation's delegation rule; who may delegate to other teams (admins only). */
  delegation?: { withinTeams: boolean; crossTeamUserIds: string[] };
}

export const teamsApi = {
  get: (includeArchived = false) =>
    apiClient.get<TeamsView>('/teams', { params: { includeArchived: includeArchived || undefined, forExecutiveUserId: null } }).then(r => r.data),
  create: (data: { name: string; description?: string; parentTeamId?: string; leadUserId?: string }) =>
    apiClient.post<Team>('/teams', data, { params: { forExecutiveUserId: null } }).then(r => r.data),
  /** Null leaves a field; an empty string clears the description or lead, or moves the team to the top. */
  update: (id: string, data: { name?: string; description?: string; parentTeamId?: string; leadUserId?: string }) =>
    apiClient.put<Team>(`/teams/${id}`, data, { params: { forExecutiveUserId: null } }).then(r => r.data),
  archive: (id: string) => apiClient.post<Team>(`/teams/${id}/archive`, null, { params: { forExecutiveUserId: null } }).then(r => r.data),
  restore: (id: string) => apiClient.post<Team>(`/teams/${id}/restore`, null, { params: { forExecutiveUserId: null } }).then(r => r.data),
  /** Keep delegation within teams, or open it to anyone in the organisation. */
  setDelegation: (withinTeams: boolean) =>
    apiClient.put<{ withinTeams: boolean }>('/teams/delegation', { withinTeams }, { params: { forExecutiveUserId: null } }).then(r => r.data),
  addMember: (id: string, userId: string) =>
    apiClient.put<Team>(`/teams/${id}/members/${encodeURIComponent(userId)}`, null, { params: { forExecutiveUserId: null } }).then(r => r.data),
  removeMember: (id: string, userId: string) =>
    apiClient.delete<Team>(`/teams/${id}/members/${encodeURIComponent(userId)}`, { params: { forExecutiveUserId: null } }).then(r => r.data),
  /** An empty value clears the line. */
  setManager: (userId: string, reportsToUserId: string) =>
    apiClient.put(`/teams/people/${encodeURIComponent(userId)}/manager`, { reportsToUserId }, { params: { forExecutiveUserId: null } }).then(r => r.data),
};

// Decisions (Decision Log + Decision Queue + Phase 3 task 3.8 traceability)
export interface DecisionTraceability {
  meetingSource?: string;
  calendarEventId?: string;
  transcriptId?: string;
  evidence?: string;
  outcomeReviewDate?: string;
}

export const decisionsApi = {
  getAll: (forExecId?: string, driverId?: string, projectId?: string) =>
    apiClient.get<Decision[]>('/decisions', { params: { forExecutiveUserId: forExecId, driverId, projectId } }).then(r => r.data),
  getPending: (forExecId?: string) =>
    apiClient.get<Decision[]>('/decisions/pending', { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  // The backend has had DELETE /decisions/{id} (owner-scoped) all along; the
  // client just never exposed it, so the decision log was append-only in
  // practice — a mistyped or duplicate entry could be edited but never removed.
  // Note a decision captured from a work item is linked by WorkItem.DecisionId
  // with OnDelete(SetNull): deleting the record unlinks the work item, which
  // correctly makes it "unrecorded" again rather than orphaning anything.
  delete: (id: string, forExecId?: string) =>
    apiClient.delete(`/decisions/${id}`, { params: { forExecutiveUserId: forExecId } }),
  create: (
    data: { title: string; rationale?: string; stakeholders?: string; decisionDate?: string; strategicDriverId?: string; projectId?: string; status?: string; reviewDate?: string; reviewReason?: string } & DecisionTraceability,
    forExecId?: string,
  ) =>
    apiClient.post<Decision>('/decisions', data, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  update: (
    id: string,
    data: Partial<{ title: string; rationale: string; stakeholders: string; decisionDate: string; strategicDriverId: string; projectId: string; status: string; reviewDate: string; reviewReason: string }> & Partial<DecisionTraceability>,
    forExecId?: string,
  ) =>
    apiClient.put<Decision>(`/decisions/${id}`, data, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  queue: (data: { title: string; rationale?: string; stakeholders?: string; decisionDate?: string; strategicDriverId?: string; projectId?: string }, forExecId?: string) =>
    apiClient.post<Decision>('/decisions/queue', data, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  resolve: (id: string, data: { resolution: string; notes?: string }, forExecId?: string) =>
    apiClient.post<Decision>(`/decisions/${id}/resolve`, data, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
};

// CSV + PDF Exports
export const exportApi = {
  downloadWorkItemsCsv: (forExecId?: string) =>
    apiClient.get('/export/work-items/csv', { responseType: 'blob', params: { forExecutiveUserId: forExecId } }).then(r => r),
  downloadProjectsCsv: (forExecId?: string) =>
    apiClient.get('/export/projects/csv', { responseType: 'blob', params: { forExecutiveUserId: forExecId } }).then(r => r),
  downloadKpiHistoryCsv: (driverId?: string, forExecId?: string) =>
    apiClient.get('/export/kpi-history/csv', { responseType: 'blob', params: { driverId, forExecutiveUserId: forExecId } }).then(r => r),

  // Weekly summary PDF — POST variant accepts a leader's note + narrative
  // override. Returns the full response so the caller can trigger a browser
  // download with the right filename from the content-disposition header.
  downloadWeeklySummaryPdf: (
    date: string,
    payload: { leaderNote?: string; whatHappened?: string; whatItMeans?: string; whatsNext?: string } = {},
    forExecId?: string,
  ) =>
    apiClient.post(`/export/weekly-summary/${date}`, payload, {
      responseType: 'blob',
      params: { forExecutiveUserId: forExecId },
    }),
  // Per-meeting briefing pack PDF — Phase 3 task 3.2. Returns the full
  // Axios response so the caller can read the content-disposition header.
  downloadBriefingPackPdf: (eventId: string, forExecId?: string) =>
    apiClient.get(`/export/briefing-pack/${eventId}`, {
      responseType: 'blob',
      params: { forExecutiveUserId: forExecId },
    }),
  // Handover pack PDF — Phase 3 task 3.5 second slice. The consolidated
  // pack an EA hands to a covering EA before going on leave. Scoped to
  // the calling EA's principals only; never accepts forExecutiveUserId
  // because the pack itself is the cross-principal artefact.
  downloadHandoverPackPdf: (payload: { coverEaName?: string; notesForCoverEa?: string } = {}) =>
    apiClient.post('/export/handover-pack', payload, {
      responseType: 'blob',
    }),
};

// Feature Requests
export const featureRequestsApi = {
  getAll: () => apiClient.get('/featurerequests').then(r => r.data),
  create: (data: any) => apiClient.post('/featurerequests', data).then(r => r.data),
  vote: (id: string) => apiClient.post(`/featurerequests/${id}/vote`).then(r => r.data),
};

// Projects
export const projectsApi = {
  getAll: (params?: { status?: string; driverId?: string; includeArchived?: boolean }) =>
    apiClient.get<Project[]>('/projects', { params }).then(r => r.data),
  getById: (id: string) => apiClient.get<Project>(`/projects/${id}`).then(r => r.data),
  getPortfolio: () => apiClient.get<Project[]>('/projects/portfolio').then(r => r.data),
  create: (data: Partial<Project>) => apiClient.post<Project>('/projects', data).then(r => r.data),
  update: (id: string, data: Partial<Project>) => apiClient.put<Project>(`/projects/${id}`, data).then(r => r.data),
  archive: (id: string) => apiClient.post(`/projects/${id}/archive`).then(r => r.data),
  restore: (id: string) => apiClient.post(`/projects/${id}/restore`).then(r => r.data),
  // Members
  getMembers: (id: string) => apiClient.get<ProjectMember[]>(`/projects/${id}/members`).then(r => r.data),
  addMember: (id: string, data: Partial<ProjectMember>) => apiClient.post<ProjectMember>(`/projects/${id}/members`, data).then(r => r.data),
  updateMember: (id: string, memberId: string, data: Partial<ProjectMember>) => apiClient.put<ProjectMember>(`/projects/${id}/members/${memberId}`, data).then(r => r.data),
  removeMember: (id: string, memberId: string) => apiClient.delete(`/projects/${id}/members/${memberId}`).then(r => r.data),
  // Outcomes
  getOutcomes: (id: string) => apiClient.get<ProjectOutcome[]>(`/projects/${id}/outcomes`).then(r => r.data),
  createOutcome: (id: string, data: Partial<ProjectOutcome>) => apiClient.post<ProjectOutcome>(`/projects/${id}/outcomes`, data).then(r => r.data),
  updateOutcome: (id: string, outcomeId: string, data: Partial<ProjectOutcome>) => apiClient.put<ProjectOutcome>(`/projects/${id}/outcomes/${outcomeId}`, data).then(r => r.data),
  deleteOutcome: (id: string, outcomeId: string) => apiClient.delete(`/projects/${id}/outcomes/${outcomeId}`).then(r => r.data),
  // Indicators
  getIndicators: (id: string) => apiClient.get<ProjectIndicator[]>(`/projects/${id}/indicators`).then(r => r.data),
  createIndicator: (id: string, data: Partial<ProjectIndicator>) => apiClient.post<ProjectIndicator>(`/projects/${id}/indicators`, data).then(r => r.data),
  updateIndicator: (id: string, indicatorId: string, data: Partial<ProjectIndicator>) => apiClient.put<ProjectIndicator>(`/projects/${id}/indicators/${indicatorId}`, data).then(r => r.data),
  deleteIndicator: (id: string, indicatorId: string) => apiClient.delete(`/projects/${id}/indicators/${indicatorId}`).then(r => r.data),
  recordValue: (id: string, indicatorId: string, data: { value: number; notes?: string }) => apiClient.post<IndicatorSnapshot>(`/projects/${id}/indicators/${indicatorId}/record`, data).then(r => r.data),
  getHistory: (id: string, indicatorId: string) => apiClient.get<IndicatorSnapshot[]>(`/projects/${id}/indicators/${indicatorId}/history`).then(r => r.data),
  // Report
  getReport: (id: string) => apiClient.get(`/projects/${id}/report`).then(r => r.data),
};

// Integrations (Slack/Teams)
export const integrationsApi = {
  getAll: () => apiClient.get<TenantIntegration[]>('/integrations').then(r => r.data),
  create: (data: { provider: string; webhookUrl: string; notifyPlanProposed?: boolean; notifyOverdueItems?: boolean; notifyDelegations?: boolean; notifyKpiAlerts?: boolean }) =>
    apiClient.post<TenantIntegration>('/integrations', data).then(r => r.data),
  update: (id: string, data: Partial<TenantIntegration>) =>
    apiClient.put<TenantIntegration>(`/integrations/${id}`, data).then(r => r.data),
  delete: (id: string) => apiClient.delete(`/integrations/${id}`),
  test: (id: string) => apiClient.post<{ message: string }>(`/integrations/${id}/test`).then(r => r.data),
};

// Calendar Conflicts
export const calendarConflictsApi = {
  getByDate: (date: string, forExecId?: string) =>
    apiClient.get<CalendarConflict[]>('/calendar/conflicts', { params: { date, forExecutiveUserId: forExecId, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone } }).then(r => r.data),
};

// Out-of-office (Phase 3 task 3.11)
export const outOfOfficeApi = {
  getAll: (forExecId?: string | null) =>
    apiClient.get<OutOfOffice[]>('/outofoffice', { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  getActive: (from?: string, to?: string, forExecId?: string | null) =>
    apiClient.get<OutOfOffice[]>('/outofoffice/active', {
      params: { from, to, forExecutiveUserId: forExecId },
    }).then(r => r.data),
  create: (
    data: { startDate: string; endDate: string; reason?: string; notes?: string; suppressBriefing?: boolean; blockWorkAllocation?: boolean },
    forExecId?: string | null,
  ) =>
    apiClient.post<OutOfOffice>('/outofoffice', data, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  update: (
    id: string,
    data: { startDate: string; endDate: string; reason?: string; notes?: string; suppressBriefing?: boolean; blockWorkAllocation?: boolean },
    forExecId?: string | null,
  ) =>
    apiClient.put<OutOfOffice>(`/outofoffice/${id}`, data, { params: { forExecutiveUserId: forExecId } }).then(r => r.data),
  delete: (id: string, forExecId?: string | null) =>
    apiClient.delete(`/outofoffice/${id}`, { params: { forExecutiveUserId: forExecId } }),
};

// Ideas (quick-capture with AI alignment assessment)
export const ideasApi = {
  getAll: (status?: IdeaStatus) =>
    apiClient.get<Idea[]>('/ideas', { params: status ? { status } : {} }).then(r => r.data),
  getById: (id: string) => apiClient.get<Idea>(`/ideas/${id}`).then(r => r.data),
  create: (data: { title: string; content?: string }) =>
    apiClient.post<Idea>('/ideas', data).then(r => r.data),
  update: (id: string, data: { title?: string; content?: string; status?: IdeaStatus }) =>
    apiClient.put<Idea>(`/ideas/${id}`, data).then(r => r.data),
  delete: (id: string) => apiClient.delete(`/ideas/${id}`),
  assess: (id: string) => apiClient.post<Idea>(`/ideas/${id}/assess`).then(r => r.data),
  convert: (id: string, data: { title?: string; description?: string; type?: string; priority?: string; estimatedMinutes?: number; targetDate?: string }) =>
    apiClient.post(`/ideas/${id}/convert`, data).then(r => r.data),
};

// External task sync — Jira / Linear / Asana integration configs. Migrated
// off the raw axios + sessionStorage pattern that the PreferencesPage
// section used to do inline; now uses the shared apiClient which handles
// token injection + refresh consistently with the rest of the SPA. The
// `ExternalSyncConfig` shape is intentionally permissive — the section
// has its own internal typing for the form state.
export interface ExternalSyncConfig {
  id: string;
  provider: 'Jira' | 'Linear' | 'Asana';
  apiUrl?: string;
  apiToken?: string;
  projectKey?: string;
  filterQuery?: string;
  autoImport: boolean;
  isEnabled: boolean;
  lastSyncedAt?: string;
}

// Your own task-sync connections, set up in Settings: never the executive an
// EA happens to be acting for (an explicit null stops the client adding one).
const OWN = { params: { forExecutiveUserId: null } };
export const externalTaskSyncApi = {
  list: () => apiClient.get<ExternalSyncConfig[]>('/external-sync', OWN).then(r => r.data),
  create: (data: Partial<ExternalSyncConfig>) =>
    apiClient.post<ExternalSyncConfig>('/external-sync', { ...data, isEnabled: true }, OWN).then(r => r.data),
  update: (id: string, data: Partial<ExternalSyncConfig>) =>
    apiClient.put<ExternalSyncConfig>(`/external-sync/${id}`, data, OWN).then(r => r.data),
  delete: (id: string) => apiClient.delete(`/external-sync/${id}`, OWN),
  test: (id: string) =>
    apiClient.post<{ success: boolean; message?: string; error?: string }>(`/external-sync/${id}/test`, null, OWN).then(r => r.data),
  sync: (id: string) =>
    apiClient.post<{ success: boolean; message?: string }>(`/external-sync/${id}/sync`, null, OWN).then(r => r.data),
};

// Review (web app v4, Phase 2.3): how many things wait for a yes, for the
// nav badge. forExecutiveUserId left undefined follows the active principal.
export const reviewApi = {
  summary: (forExecutiveUserId?: string | null) =>
    apiClient.get<ReviewSummary>('/review/summary', { params: { forExecutiveUserId } }).then(r => r.data),
};

// Voice notes captured on the phone, watch or Mac (needs voiceCapture).
// Review lists the pending ones; committing makes their work items.
export const voiceCapturesApi = {
  /** Newest first; pending = not yet committed or discarded. */
  list: (params?: { pending?: boolean; limit?: number }) =>
    apiClient.get<VoiceCapture[]>('/captures/voice', { params: { pending: params?.pending, limit: params?.limit } }).then(r => r.data),
  updateCandidate: (candidateId: string, changes: PatchVoiceCandidateRequest) =>
    apiClient.patch<VoiceCandidate>(`/captures/voice/candidate/${candidateId}`, changes).then(r => r.data),
  /** Makes the chosen candidates' work items (all when none are named); the note is then done. */
  commit: (id: string, candidateIds?: string[]) =>
    apiClient.post<VoiceCapture>(`/captures/voice/${id}/commit`, { candidateIds: candidateIds ?? null }).then(r => r.data),
  discard: (id: string) => apiClient.delete(`/captures/voice/${id}`),
};
